import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Section from '../components/ui/Section';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import AnimatedSection from '../components/ui/AnimatedSection';
import SEOHelmet from '../seo/SEOHelmet';
import { useLanguage } from '../i18n/LanguageContext';
import HeaderBannerComponent from '../components/ui/HeaderBanner';

// Dati di esempio per gli alloggi
const accommodations = [
  {
    id: 1,
    title: {
      it: 'Villa Toscana',
      en: 'Tuscan Villa'
    },
    location: {
      it: 'Firenze, Toscana',
      en: 'Florence, Tuscany'
    },
    price: {
      it: '€150/notte',
      en: '€150/night'
    },
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    images: [
      'https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'
    ],
    beds: 4,
    baths: 2,
    size: '180 m²',
    type: 'villa',
    description: {
      it: 'Elegante villa situata sulle colline toscane con vista panoramica sulla città di Firenze. La proprietà è circondata da uliveti e vigneti, offrendo un\'atmosfera tranquilla e rilassante.',
      en: 'Elegant villa located on the Tuscan hills with panoramic views of the city of Florence. The property is surrounded by olive groves and vineyards, offering a peaceful and relaxing atmosphere.'
    },
    amenities: [
      {
        it: 'Piscina',
        en: 'Swimming Pool'
      }, 
      {
        it: 'Wi-Fi',
        en: 'Wi-Fi'
      }, 
      {
        it: 'Aria condizionata',
        en: 'Air Conditioning'
      }, 
      {
        it: 'Parcheggio',
        en: 'Parking'
      }, 
      {
        it: 'Giardino',
        en: 'Garden'
      }, 
      {
        it: 'Terrazza',
        en: 'Terrace'
      }
    ]
  },
  {
    id: 2,
    title: {
      it: 'Appartamento nel Centro Storico',
      en: 'Historic Center Apartment'
    },
    location: {
      it: 'Siena, Toscana',
      en: 'Siena, Tuscany'
    },
    price: {
      it: '€120/notte',
      en: '€120/night'
    },
    image: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    images: [
      'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      'https://images.unsplash.com/photo-1493809842364-78817add7ffb?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      'https://images.unsplash.com/photo-1560448204-603b3fc33ddc?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'
    ],
    beds: 2,
    baths: 1,
    size: '85 m²',
    type: 'apartment',
    description: {
      it: 'Appartamento ristrutturato nel cuore del centro storico di Siena, a pochi passi da Piazza del Campo. L\'immobile mantiene i dettagli architettonici originali come travi a vista e pavimenti in cotto.',
      en: 'Renovated apartment in the heart of the historic center of Siena, a few steps from Piazza del Campo. The property maintains original architectural details such as exposed beams and terracotta floors.'
    },
    amenities: [
      {
        it: 'Wi-Fi',
        en: 'Wi-Fi'
      }, 
      {
        it: 'Aria condizionata',
        en: 'Air Conditioning'
      }, 
      {
        it: 'Cucina completa',
        en: 'Full Kitchen'
      }, 
      {
        it: 'Lavatrice',
        en: 'Washing Machine'
      }
    ]
  },
  {
    id: 3,
    title: {
      it: 'Casa sulla Spiaggia',
      en: 'Beach House'
    },
    location: {
      it: 'Viareggio, Toscana',
      en: 'Viareggio, Tuscany'
    },
    price: {
      it: '€180/notte',
      en: '€180/night'
    },
    image: 'https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    images: [
      'https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      'https://images.unsplash.com/photo-1464146072230-91cabc968266?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'
    ],
    beds: 3,
    baths: 2,
    size: '120 m²',
    type: 'villa',
    description: {
      it: 'Casa moderna a pochi passi dalla spiaggia con splendida vista sul mare. Ampia terrazza con zona pranzo all\'aperto e accesso diretto alla passeggiata sul lungomare.',
      en: 'Modern house just steps from the beach with stunning sea views. Large terrace with outdoor dining area and direct access to the seafront promenade.'
    },
    amenities: [
      {
        it: 'Vista mare',
        en: 'Sea View'
      }, 
      {
        it: 'Wi-Fi',
        en: 'Wi-Fi'
      }, 
      {
        it: 'Aria condizionata',
        en: 'Air Conditioning'
      }, 
      {
        it: 'Parcheggio',
        en: 'Parking'
      }, 
      {
        it: 'Terrazza',
        en: 'Terrace'
      }, 
      {
        it: 'BBQ',
        en: 'BBQ'
      }
    ]
  },
  {
    id: 4,
    title: {
      it: 'Cascina Ristrutturata',
      en: 'Renovated Farmhouse'
    },
    location: {
      it: 'Lucca, Toscana',
      en: 'Lucca, Tuscany'
    },
    price: {
      it: '€200/notte',
      en: '€200/night'
    },
    image: 'https://images.unsplash.com/photo-1510798831971-661eb04b3739?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    images: [
      'https://images.unsplash.com/photo-1510798831971-661eb04b3739?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      'https://images.unsplash.com/photo-1599427303058-f04cbcf4756f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      'https://images.unsplash.com/photo-1505916349660-8d91a99c3e23?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'
    ],
    beds: 5,
    baths: 3,
    size: '250 m²',
    type: 'villa',
    description: {
      it: 'Antica cascina completamente ristrutturata immersa nella campagna lucchese. La proprietà è circondata da un ampio giardino con piscina privata e zona relax.',
      en: 'Ancient farmhouse completely renovated immersed in the Lucca countryside. The property is surrounded by a large garden with private swimming pool and relaxation area.'
    },
    amenities: [
      {
        it: 'Piscina',
        en: 'Swimming Pool'
      }, 
      {
        it: 'Wi-Fi',
        en: 'Wi-Fi'
      }, 
      {
        it: 'Aria condizionata',
        en: 'Air Conditioning'
      }, 
      {
        it: 'Parcheggio',
        en: 'Parking'
      }, 
      {
        it: 'Giardino',
        en: 'Garden'
      }, 
      {
        it: 'Camino',
        en: 'Fireplace'
      }
    ]
  },
  {
    id: 5,
    title: {
      it: 'Loft Moderno',
      en: 'Modern Loft'
    },
    location: {
      it: 'Genova, Liguria',
      en: 'Genoa, Liguria'
    },
    price: {
      it: '€130/notte',
      en: '€130/night'
    },
    image: 'https://images.unsplash.com/photo-1536376072261-38c75010e6c9?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    images: [
      'https://images.unsplash.com/photo-1536376072261-38c75010e6c9?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      'https://images.unsplash.com/photo-1513694203232-719a280e022f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'
    ],
    beds: 2,
    baths: 1,
    size: '90 m²',
    type: 'apartment',
    description: {
      it: 'Loft di design nel quartiere del porto antico di Genova. Spazi aperti con arredamento contemporaneo e finiture di pregio. A pochi passi dall\'Acquario.',
      en: 'Design loft in the ancient port district of Genoa. Open spaces with contemporary furniture and fine finishes. A few steps from the Aquarium.'
    },
    amenities: [
      {
        it: 'Wi-Fi',
        en: 'Wi-Fi'
      }, 
      {
        it: 'Aria condizionata',
        en: 'Air Conditioning'
      }, 
      {
        it: 'Smart TV',
        en: 'Smart TV'
      }, 
      {
        it: 'Cucina completa',
        en: 'Full Kitchen'
      }, 
      {
        it: 'Ascensore',
        en: 'Elevator'
      }
    ]
  },
  {
    id: 6,
    title: {
      it: 'Villetta con Giardino',
      en: 'Villa with Garden'
    },
    location: {
      it: 'Rapallo, Liguria',
      en: 'Rapallo, Liguria'
    },
    price: {
      it: '€160/notte',
      en: '€160/night'
    },
    image: 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    images: [
      'https://images.unsplash.com/photo-1518780664697-55e3ad937233?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      'https://images.unsplash.com/photo-1523217582562-09d0def993a6?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      'https://images.unsplash.com/photo-1516156008625-3a9d6067fab5?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'
    ],
    beds: 3,
    baths: 2,
    size: '140 m²',
    type: 'villa',
    description: {
      it: 'Graziosa villetta situata nelle colline di Rapallo con vista sul Golfo del Tigullio. Giardino privato con zona pranzo all\'aperto e piccola piscina.',
      en: 'Charming villa located in the hills of Rapallo with views of the Gulf of Tigullio. Private garden with outdoor dining area and small swimming pool.'
    },
    amenities: [
      {
        it: 'Piscina',
        en: 'Swimming Pool'
      }, 
      {
        it: 'Wi-Fi',
        en: 'Wi-Fi'
      }, 
      {
        it: 'Aria condizionata',
        en: 'Air Conditioning'
      }, 
      {
        it: 'Parcheggio',
        en: 'Parking'
      }, 
      {
        it: 'Giardino',
        en: 'Garden'
      }, 
      {
        it: 'Vista mare',
        en: 'Sea View'
      }
    ]
  },
  {
    id: 7,
    title: {
      it: 'Castello Medievale',
      en: 'Medieval Castle'
    },
    location: {
      it: 'Siena, Toscana',
      en: 'Siena, Tuscany'
    },
    price: {
      it: '€500/notte',
      en: '€500/night'
    },
    image: 'https://images.unsplash.com/photo-1585543805890-6051f7829f98?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    images: [
      'https://images.unsplash.com/photo-1585543805890-6051f7829f98?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      'https://images.unsplash.com/photo-1519074069444-1ba4fff66d16?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      'https://images.unsplash.com/photo-1588880331179-bc9b93a8cb5e?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'
    ],
    beds: 8,
    baths: 6,
    size: '600 m²',
    type: 'castle',
    description: {
      it: 'Autentico castello medievale perfettamente restaurato situato su una collina con vista panoramica. Interni lussuosi con elementi originali e arredamento d\'epoca.',
      en: 'Authentic medieval castle perfectly restored located on a hill with panoramic views. Luxurious interiors with original elements and period furnishings.'
    },
    amenities: [
      {
        it: 'Piscina',
        en: 'Swimming Pool'
      },
      {
        it: 'Giardino Privato',
        en: 'Private Garden'
      },
      {
        it: 'Cantina di Vini',
        en: 'Wine Cellar'
      },
      {
        it: 'Cappella Privata',
        en: 'Private Chapel'
      },
      {
        it: 'Personale di Servizio',
        en: 'Service Staff'
      }
    ]
  },
  {
    id: 8,
    title: {
      it: 'Attico di Lusso',
      en: 'Luxury Penthouse'
    },
    location: {
      it: 'Firenze, Toscana',
      en: 'Florence, Tuscany'
    },
    price: {
      it: '€320/notte',
      en: '€320/night'
    },
    image: 'https://images.unsplash.com/photo-1502672023488-70e25813eb80?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    images: [
      'https://images.unsplash.com/photo-1502672023488-70e25813eb80?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      'https://images.unsplash.com/photo-1567016376408-0226e4d0b1ea?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      'https://images.unsplash.com/photo-1533779283484-8ad4940aa3a8?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'
    ],
    beds: 3,
    baths: 2,
    size: '180 m²',
    type: 'penthouse',
    description: {
      it: 'Elegante attico nel centro di Firenze con ampie terrazze e vista spettacolare sul Duomo. Interni di design con finiture di pregio e tecnologia all\'avanguardia.',
      en: 'Elegant penthouse in the center of Florence with large terraces and spectacular views of the Duomo. Designer interiors with fine finishes and cutting-edge technology.'
    },
    amenities: [
      {
        it: 'Terrazza Panoramica',
        en: 'Panoramic Terrace'
      },
      {
        it: 'Jacuzzi',
        en: 'Jacuzzi'
      },
      {
        it: 'Sistema Domotico',
        en: 'Home Automation'
      },
      {
        it: 'Ascensore Privato',
        en: 'Private Elevator'
      },
      {
        it: 'Concierge 24h',
        en: '24h Concierge'
      }
    ]
  }
];

const MapSection = styled.div`
  margin-bottom: 4rem;
`;

const MapTitle = styled.h4`
  font-size: 1.8rem;
  margin-bottom: 2rem;
  text-align: center;
`;

const MapPlaceholder = styled.div`
  width: 100%;
  height: 400px;
  background-color: var(--bg-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const AccommodationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 2.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FilterBar = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 3rem;
  gap: 1.5rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const FilterButton = styled.button<{ $active: boolean }>`
  padding: 0.75rem 2rem;
  background-color: ${({ $active }) => $active ? 'var(--accent-primary)' : 'transparent'};
  color: ${({ $active }) => $active ? 'white' : 'var(--text-primary)'};
  border: 1px solid ${({ $active }) => $active ? 'var(--accent-primary)' : 'rgba(255, 255, 255, 0.3)'};
  border-radius: 30px;
  cursor: pointer;
  font-weight: ${({ $active }) => $active ? '600' : '400'};
  transition: all 0.3s ease;
  /* Aggiunto per accessibilità */
  aria-pressed: ${({ $active }) => $active ? 'true' : 'false'};
  
  &:hover {
    background-color: ${({ $active }) => $active ? 'var(--accent-primary)' : 'rgba(255, 255, 255, 0.1)'};
    border-color: ${({ $active }) => $active ? 'var(--accent-primary)' : 'rgba(255, 255, 255, 0.5)'};
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const ModalBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
`;

const ModalContent = styled(motion.div)`
  background-color: var(--bg-secondary);
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  position: relative;
  height: 350px;
  overflow: hidden;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
`;

const ModalImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const ModalImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const ImageNavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.8);
    transform: translateY(-50%) scale(1.1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrevButton = styled(ImageNavButton)`
  left: 1rem;
`;

const NextButton = styled(ImageNavButton)`
  right: 1rem;
`;

const ImageIndicators = styled.div`
  position: absolute;
  bottom: 70px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 8px;
  z-index: 10;
`;

const ImageIndicator = styled.div<{ $active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ $active }) => $active ? 'white' : 'rgba(255, 255, 255, 0.5)'};
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    background-color: ${({ $active }) => $active ? 'white' : 'rgba(255, 255, 255, 0.7)'};
  }
`;

const ModalCloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.8);
    transform: rotate(90deg);
  }
`;

const ModalBody = styled.div`
  padding: 2.5rem;
`;

const ModalTitle = styled.h2`
  font-size: 2.2rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const ModalLocation = styled.p`
  color: var(--text-secondary);
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  font-size: 1.1rem;
`;

const ModalPrice = styled.div`
  font-size: 1.6rem;
  font-weight: 600;
  color: var(--accent-primary);
  margin-bottom: 2rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const ModalSpecs = styled.div`
  display: flex;
  justify-content: space-around;
  margin-bottom: 2.5rem;
  padding: 1.5rem;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  color: var(--text-secondary);
`;

const ModalSpecItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ModalDescription = styled.p`
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const ModalAmenities = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 2.5rem;
  padding: 1.5rem;
  background-color: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
`;

const ModalAmenitiesTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 1rem;
`;

const ModalAmenitiesList = styled.ul`
  list-style: none;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1rem;
`;

const ModalAmenityItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.5rem;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
    border-radius: 6px;
  }
  
  svg {
    color: var(--accent-primary);
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 2rem;
  gap: 1rem;
`;

const ShareButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  color: var(--text-primary);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 30px;
  padding: 0.75rem 1.5rem;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
    transform: translateY(-2px);
  }
  
  svg {
    margin-right: 0.5rem;
  }
`;

interface LocalizedContent {
  it: string;
  en: string;
}

interface Accommodation {
  id: number;
  title: LocalizedContent;
  location: LocalizedContent;
  price: LocalizedContent;
  image: string;
  images: string[];
  beds: number;
  baths: number;
  size: string;
  type: string;
  description: LocalizedContent;
  amenities: LocalizedContent[];
}

const Accommodations: React.FC = () => {
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [selectedAccommodation, setSelectedAccommodation] = useState<Accommodation | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  const { t, language } = useLanguage();
  
  const handleOpenModal = (accommodation: Accommodation) => {
    setSelectedAccommodation(accommodation);
    setCurrentImageIndex(0);
    setIsModalOpen(true);
  };
  
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };
  
  const handlePrevImage = () => {
    if (selectedAccommodation && currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };
  
  const handleNextImage = () => {
    if (selectedAccommodation && currentImageIndex < selectedAccommodation.images.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    }
  };
  
  const handleImageIndicatorClick = (index: number) => {
    setCurrentImageIndex(index);
  };
  
  // Funzione di supporto per il rendering dei contenuti multilingua
  const getLocalizedContent = (content: any) => {
    if (content && typeof content === 'object' && content[language]) {
      return content[language];
    }
    return content;
  };

  // Filtraggio basato su tipo di proprietà e regione
  const filteredAccommodations = accommodations.filter(acc => {
    // Filtro per tipo di proprietà
    const typeMatch = selectedFilter === 'all' || 
      (selectedFilter === 'penthouse' && acc.type === 'penthouse') ||
      (selectedFilter === 'castle' && acc.type === 'castle') ||
      (selectedFilter === 'villa' && acc.type === 'villa') ||
      (selectedFilter === 'apartment' && acc.type === 'apartment') ||
      (selectedFilter === 'terrace' && acc.amenities.some(am => 
        getLocalizedContent(am).toLowerCase().includes('terrazza') || 
        getLocalizedContent(am).toLowerCase().includes('terrace')));
    
    return typeMatch;
  });
  
  // SEO schema data per gli alloggi
  const listingSchemaData = {
    numberOfRooms: accommodations.reduce((total, acc) => total + acc.beds, 0),
    accommodationCategory: 'Luxury',
    amenityFeature: Array.from(
      new Set(accommodations.flatMap(acc => acc.amenities.map(amenity => getLocalizedContent(amenity))))
    ).map(amenity => ({
      '@type': 'LocationFeatureSpecification',
      name: amenity
    }))
  };
  
  return (
    <>
      <SEOHelmet
        title={t('accommodations.page_title')}
        description={t('accommodations.description')}
        canonical="/accommodations"
        keywords={t('accommodations.seo_keywords').split(', ')}
      />
      
      <HeaderBannerComponent />
      
      <Section 
        title={t('accommodations.title')}
        padding="5rem 0"
      >
        <FilterBar>
          <FilterButton 
            $active={selectedFilter === 'all'} 
            onClick={() => setSelectedFilter('all')}
          >
            {t('accommodations.filter.all')}
          </FilterButton>
          <FilterButton 
            $active={selectedFilter === 'villa'} 
            onClick={() => setSelectedFilter('villa')}
          >
            {t('accommodations.filter.villas')}
          </FilterButton>
          <FilterButton 
            $active={selectedFilter === 'apartment'} 
            onClick={() => setSelectedFilter('apartment')}
          >
            {t('accommodations.filter.apartments')}
          </FilterButton>
          <FilterButton 
            $active={selectedFilter === 'penthouse'} 
            onClick={() => setSelectedFilter('penthouse')}
          >
            {t('accommodations.filter.penthouses')}
          </FilterButton>
          <FilterButton 
            $active={selectedFilter === 'castle'} 
            onClick={() => setSelectedFilter('castle')}
          >
            {t('accommodations.filter.castles')}
          </FilterButton>
          <FilterButton 
            $active={selectedFilter === 'terrace'} 
            onClick={() => setSelectedFilter('terrace')}
          >
            {language === 'it' ? 'Terrazze' : 'Terraces'}
          </FilterButton>
        </FilterBar>
        
        <MapSection>
          <MapTitle>{t('accommodations.map_title')}</MapTitle>
          <MapPlaceholder>
            <div style={{ textAlign: 'center', opacity: 0.6 }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 22C14 18 20 15.4183 20 10C20 5.58172 16.4183 2 12 2C7.58172 2 4 5.58172 4 10C4 15.4183 10 18 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <p style={{ marginTop: '1rem' }}>{language === 'it' ? 'Mappa in fase di caricamento...' : 'Map loading...'}</p>
            </div>
          </MapPlaceholder>
        </MapSection>
        
        <AccommodationGrid>
          {filteredAccommodations.map((accommodation) => (
            <AnimatedSection key={accommodation.id}>
              <Card
                image={accommodation.image}
                title={getLocalizedContent(accommodation.title)}
                subtitle={getLocalizedContent(accommodation.location)}
                price={getLocalizedContent(accommodation.price)}
                description={`${accommodation.beds} ${t('accommodations.beds')} · ${accommodation.baths} ${t('bathrooms')} · ${accommodation.size}`}
                hoverEffect={true}
                onClick={() => handleOpenModal(accommodation)}
                tags={accommodation.amenities.slice(0, 3).map(amenity => getLocalizedContent(amenity))}
              />
            </AnimatedSection>
          ))}
        </AccommodationGrid>
        
        {isModalOpen && selectedAccommodation && (
          <ModalBackground onClick={handleCloseModal}>
            <ModalContent
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1.0] }}
            >
              <ModalHeader>
                <ModalImageContainer>
                  <ModalImage 
                    src={selectedAccommodation.images[currentImageIndex]} 
                    alt={getLocalizedContent(selectedAccommodation.title)} 
                  />
                  
                  <PrevButton 
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePrevImage();
                    }}
                    disabled={currentImageIndex === 0}
                  >
                    &#10094;
                  </PrevButton>
                  
                  <NextButton 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNextImage();
                    }}
                    disabled={currentImageIndex === selectedAccommodation.images.length - 1}
                  >
                    &#10095;
                  </NextButton>
                  
                  <ImageIndicators>
                    {selectedAccommodation.images.map((_, index) => (
                      <ImageIndicator 
                        key={index} 
                        $active={currentImageIndex === index}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleImageIndicatorClick(index);
                        }}
                      />
                    ))}
                  </ImageIndicators>
                </ModalImageContainer>
                
                <ModalCloseButton onClick={handleCloseModal}>×</ModalCloseButton>
                <div style={{ 
                  position: 'absolute', 
                  bottom: 0, 
                  left: 0, 
                  right: 0, 
                  background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
                  padding: '2rem 1.5rem 1rem'
                }}>
                  <h2 style={{ color: 'white', margin: 0, fontSize: '2.2rem' }}>{getLocalizedContent(selectedAccommodation.title)}</h2>
                  <p style={{ color: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0.5rem 0 0 0' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 22C14 18 20 15.4183 20 10C20 5.58172 16.4183 2 12 2C7.58172 2 4 5.58172 4 10C4 15.4183 10 18 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {getLocalizedContent(selectedAccommodation.location)}
                  </p>
                </div>
              </ModalHeader>
              
              <ModalBody>
                <ModalPrice>{getLocalizedContent(selectedAccommodation.price)}</ModalPrice>
                
                <ModalSpecs>
                  <ModalSpecItem>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 10V7C20 5.34315 18.6569 4 17 4H7C5.34315 4 4 5.34315 4 7V10M20 10V19C20 20.1046 19.1046 21 18 21H6C4.89543 21 4 20.1046 4 19V10M20 10H4M8 7H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    <span>{selectedAccommodation.beds} {t('bedrooms')}</span>
                  </ModalSpecItem>
                  
                  <ModalSpecItem>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 7C5 5.34315 6.34315 4 8 4H16C17.6569 4 19 5.34315 19 7V21H5V7Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      <path d="M19 11H5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      <path d="M9 21V17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      <path d="M12 8V8.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    <span>{selectedAccommodation.baths} {t('bathrooms')}</span>
                  </ModalSpecItem>
                  
                  <ModalSpecItem>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19 9L5 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      <path d="M19 15L5 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      <path d="M19 5L5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      <path d="M19 19L5 19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    <span>{selectedAccommodation.size}</span>
                  </ModalSpecItem>
                </ModalSpecs>
                
                <ModalDescription>
                  {getLocalizedContent(selectedAccommodation.description)}
                </ModalDescription>
                
                <ModalAmenities>
                  <ModalAmenitiesTitle>{t('amenities')}</ModalAmenitiesTitle>
                  <ModalAmenitiesList>
                    {selectedAccommodation.amenities.map((amenity, index) => (
                      <ModalAmenityItem key={index}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {getLocalizedContent(amenity)}
                      </ModalAmenityItem>
                    ))}
                  </ModalAmenitiesList>
                </ModalAmenities>
                
                <ModalFooter>
                  <ShareButton>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18 8C19.6569 8 21 6.65685 21 5C21 3.34315 19.6569 2 18 2C16.3431 2 15 3.34315 15 5C15 5.12548 15.0077 5.24917 15.0227 5.37061L8.08261 9.84066C7.54305 9.32015 6.80891 9 6 9C4.34315 9 3 10.3431 3 12C3 13.6569 4.34315 15 6 15C6.80891 15 7.54305 14.6798 8.08261 14.1593L15.0227 18.6294C15.0077 18.7508 15 18.8745 15 19C15 20.6569 16.3431 22 18 22C19.6569 22 21 20.6569 21 19C21 17.3431 19.6569 16 18 16C17.1911 16 16.457 16.3202 15.9174 16.8407L8.97733 12.3706C8.99229 12.2492 9 12.1255 9 12C9 11.8745 8.99229 11.7508 8.97733 11.6294L15.9174 7.15934C16.457 7.67985 17.1911 8 18 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {language === 'it' ? 'Condividi' : 'Share'}
                  </ShareButton>
                  <Button 
                    variant="primary" 
                    to="/contact"
                    size="large"
                  >
                    {t('contact.title')}
                  </Button>
                </ModalFooter>
              </ModalBody>
            </ModalContent>
          </ModalBackground>
        )}
      </Section>
    </>
  );
};

export default Accommodations; 
